/**
 * AuthCard — Shared wrapper for all auth pages.
 *
 * Provides consistent card styling, title, subtitle, logo, and error display.
 * Responsive: fills width on mobile, centered card on tablet+, wider on desktop.
 *
 * Props:
 *   title, subtitle, error, children, showLogo
 */
import React from 'react';
import { useTheme } from '../../theme/ThemeContext';

export default function AuthCard({ title, subtitle, error, children, showLogo = true }) {
  const { theme } = useTheme();

  // Determine if dark mode
  const isDark = theme.auth.containerBg === '#111827';

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1f2e 50%, #0E0E0F 100%)'
          : 'linear-gradient(145deg, #F5F3F0 0%, #F9F7F4 35%, #F0F4F1 65%, #F7F4F1 100%)',
        padding: '12px',
      }}
    >
      <div
        className="auth-card"
        style={{
          maxWidth: 520,
          width: '100%',
          padding: '24px 36px',
          border: `1px solid ${theme.auth.containerBorder}`,
          borderRadius: 16,
          boxShadow: isDark
            ? '0 8px 24px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.1)'
            : '0 16px 48px rgba(0,0,0,0.07), 0 2px 8px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.7)',
          background: isDark
            ? 'linear-gradient(135deg, #111827 0%, #1a2847 100%)'
            : 'linear-gradient(145deg, #FEFEFE 0%, #FAF9F7 100%)',
          color: theme.auth.containerText,
          boxSizing: 'border-box',
        }}
      >
        {/* Logo Section */}
        {showLogo && (
          <div
            style={{
              textAlign: 'center',
              marginBottom: 14,
              paddingBottom: 12,
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 6,
              }}
            >
              <img
                src="/logo.png"
                alt="ArogyaKhata"
                style={{
                  height: 52,
                  width: 'auto',
                  objectFit: 'contain',
                  filter: isDark ? 'brightness(1.1)' : 'brightness(1)',
                }}
              />
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 20,
                fontWeight: 700,
                background: isDark
                  ? 'linear-gradient(135deg, #0d9488 0%, #58A6FF 50%, #2563eb 100%)'
                  : 'linear-gradient(135deg, #0f766e 0%, #2563EB 50%, #2563eb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
              }}
            >
              ArogyaKhata
            </h1>
          </div>
        )}

        {/* Title Section */}
        {title ? (
          <h2
            style={{
              marginTop: 0,
              marginBottom: 4,
              fontSize: 19,
              fontWeight: 600,
              color: theme.auth.containerText,
              letterSpacing: '-0.3px',
            }}
          >
            {title}
          </h2>
        ) : null}
        {subtitle ? (
          <p
            style={{
              fontSize: 13,
              color: theme.auth.mutedText,
              marginTop: 2,
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </p>
        ) : null}

        {/* Error Display */}
        {error ? (
          <div
            role="alert"
            style={{
              padding: '10px 14px',
              marginBottom: 14,
              background: isDark
                ? 'rgba(220, 38, 38, 0.15)'
                : 'rgba(220, 38, 38, 0.1)',
              border: `1px solid ${isDark ? 'rgba(220, 38, 38, 0.4)' : 'rgba(220, 38, 38, 0.3)'}`,
              color: isDark ? '#FF8A80' : '#b71c1c',
              borderRadius: 8,
              fontSize: 13,
              lineHeight: 1.4,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        ) : null}

        {/* Child Content */}
        {children}
      </div>

      {/* Responsive styles injected via <style> tag */}
      <style>{`
        @media (max-width: 480px) {
          .auth-card {
            max-width: 100% !important;
            padding: 20px 16px !important;
            border-radius: 12px !important;
            margin: 0 4px;
          }
        }
        @media (min-width: 481px) and (max-width: 768px) {
          .auth-card {
            max-width: 460px !important;
            padding: 22px 28px !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .auth-card {
            max-width: 500px !important;
            padding: 24px 32px !important;
          }
        }
        @media (min-width: 1025px) {
          .auth-card {
            max-width: 520px !important;
            padding: 28px 40px !important;
          }
        }
      `}</style>
    </div>
  );
}
